import { useMemo, useState } from "react";
import { Button, Spinner, Alert, Dropdown } from "react-bootstrap";
import { FiCopy } from "react-icons/fi";
import {
  useAllMainSiteContent,
  useUpdateMainSiteContent,
} from "../../../api/main-site-content/main-site-content.hooks";
import Pagination from "../../components/Pagination/Pagination";
import FleetManagementEditor from "./_components/FleetManagementEditor";

const FleetManagementContent = () => {
  const itemsPerPage = 10;
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [showEditor, setShowEditor] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "info",
  });

  const showAlert = (message, variant = "info") => {
    setAlert({ show: true, message, variant });
    setTimeout(
      () => setAlert({ show: false, message: "", variant: "info" }),
      5000
    );
  };

  const {
    updateContent,
    isLoading: isUpdating,
  } = useUpdateMainSiteContent();

  const {
    content: fleetContent,
    isLoading,
    isError,
    refetch,
  } = useAllMainSiteContent({ type: "fleet-management-content" });

  const tableData = useMemo(() => {
    if (!fleetContent) return [];

    return fleetContent.map((item) => ({
      id: item._id,
      siteName: item.siteName || "-",
      pageName: item.pageName || "-",
      type: item.type || "fleet-management-content",
      fullItem: item,
    }));
  }, [fleetContent]);

  const filteredData = useMemo(() => {
    if (!searchTerm) return tableData;

    return tableData.filter((item) =>
      [item.siteName, item.pageName, item.type]
        .join(" ")
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
    );
  }, [tableData, searchTerm]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const paginatedData = useMemo(() => {
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage]);

  const handleEdit = (item) => {
    setSelectedPage(item);
    setShowEditor(true);
  };

  const handleSave = async (updatedData) => {
    if (!selectedPage) return;

    const success = await updateContent(selectedPage._id, updatedData);
    if (success) {
      setShowEditor(false);
      showAlert("Changes saved successfully!", "success");
      await refetch();
    } else {
      showAlert("Failed to save changes.", "danger");
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showAlert("Copied to clipboard!", "success");
    } catch (err) {
      showAlert("Failed to copy!", "danger");
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="danger" className="m-3">
        <Alert.Heading>Error!</Alert.Heading>
        <p>
          Failed to load fleet management content. Please try again later.
        </p>
      </Alert>
    );
  }

  if (showEditor && selectedPage) {
    return (
      <FleetManagementEditor
        data={selectedPage}
        onSave={handleSave}
        isLoading={isUpdating}
        onCancel={() => setShowEditor(false)}
      />
    );
  }

  return (
    <div className="col-12">
      {alert.show && (
        <Alert
          variant={alert.variant}
          dismissible
          onClose={() =>
            setAlert({ show: false, message: "", variant: "info" })
          }
          className="mb-3"
        >
          {alert.message}
        </Alert>
      )}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h4 className="card-title m-0">Fleet Management Content</h4>
          <div className="d-flex gap-2">
            <input
              type="text"
              className="form-control w-auto"
              placeholder="Search content"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Page Name</th>
                  <th>Type</th>
                  <th>Url</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item, i) => (
                  <tr key={item.id}>
                    <td>{currentPage * itemsPerPage + i + 1}</td>
                    <td>{item.pageName}</td>
                    <td>{item.type}</td>
                    <td className="d-flex align-items-center gap-2">
                      <span>/{item.pageName}</span>
                      <Button
                        variant="link"
                        onClick={() => handleCopy(`/${item.pageName}`)}
                        title="Copy URL"
                      >
                        <FiCopy />
                      </Button>
                    </td>
                    <td>
                      <Dropdown>
                        <Dropdown.Toggle variant="link">
                          &#x2022;&#x2022;&#x2022;
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end">
                          <Dropdown.Item
                            onClick={() => handleEdit(item.fullItem)}
                          >
                            Edit
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={filteredData.length}
            onPageChange={setCurrentPage}
          />

          {filteredData.length === 0 && !isLoading && (
            <div className="text-center py-4">
              <p className="text-muted">No fleet management content found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FleetManagementContent;
